import apiClient from '@/lib/api-client';
import type { Facet, Suggesties } from '@xxtract/tabelfilters';
import { toProcessFiltersQuery, type ProcessFilterValues } from './procesLijsten';

/** Taal-neutrale (systeemcode) shapes exactly as returned by the h6-1 processes BFF module. */
export type PhysicalProcessName = 'physical';
export type DienstProcessName = 'log' | 'label' | 'dangergoods' | 'marketing' | 'measure' | 'packinfo' | 'packshot' | 'labelimage';
/** Physical (aparte fysieke lens) + de acht diensten — 1-op-1 met de BFF's CANONICAL_PROCESSES. */
export type CanonicalProcessName = PhysicalProcessName | DienstProcessName;
export type CoverageState = 'covered' | 'no_data' | 'publisher_offline' | 'unmapped';
export type KpiState = 'on_time' | 'over_kpi' | 'stuck' | 'not_started';
export type SourceStatus = 'ok' | 'unavailable';
/** spec-statusfilter-processen (CAP-2): de ordertak kent ook `rejected_filter` — de bron weigerde het filter, geen storing. De gebeurtenissenbron blijft binair. */
export type ProcesManagementSourceStatus = SourceStatus | 'rejected_filter';
/** Story 6.1 (FR15/FR16, AD-19) — de optionele, afschakelbare DQS-QA-verrijkingsnaad. `ok` is gereserveerd voor de toekomstige activatiestory (OQ-4); de MVP levert uitsluitend 'degraded'/'unavailable'. */
export type DqsSourceStatus = 'ok' | 'degraded' | 'unavailable';

/** Story 3.2 (FR7, AD-15/AD-24) — stilstand-afleiding, gedeeld tussen elke stap en de proces-brede landing-tegel. */
export interface Stagnation {
  unit: 'products';
  count: number;
  thresholdWorkdays: number;
  basis: 'workdays_since_receipt';
}

export interface FunnelStep {
  canonicalStep: string;
  count: number;
  stagnation: Stagnation;
}

/** Story 3.3 (FR8, AD-15 knelpuntregel) — de deterministisch bepaalde knelpunt-stap binnen één canoniek proces. */
export interface BottleneckStep {
  canonicalStep: string;
  stagnationCount: number;
  thresholdWorkdays: number;
}

export interface KpiSummary {
  meanWorkdays: number | null;
  pctWithinKpi: number | null;
  overKpiCount: number;
  stuckCount: number;
  notReceivedCount: number;
  total: number;
}

export interface LeadTime {
  ms: number | null;
  basis: 'settled' | 'running';
  spread: number | null;
}

/** Story 4.1 (AD-22) — een bron met een eigen cache-TTL draagt zijn eigen `fetchedAt`, niet alleen de page-level top-level stempel. */
export interface EventsSourceStatus {
  status: SourceStatus;
  fetchedAt: string;
}

export interface Sources {
  procesManagement: ProcesManagementSourceStatus;
  /** Story 4.1 (AD-16) — het zware `/events`-tijd-per-stap-pad; uitsluitend aanwezig op proces-detail. */
  procesManagementEvents?: EventsSourceStatus;
  /** Story 6.1 (FR15/FR16, AD-19/AD-23) — de optionele DQS-QA-verrijkingsnaad, gedegradeerd in de MVP (OQ-4). */
  dqs?: DqsSourceStatus;
}

export type StepTimingState = 'measured' | 'insufficient_data';

/** Story 4.1 (FR11/FR21, AD-16) — gemiddelde werkdagen-vanaf-ontvangst per stap. */
export interface StepTiming {
  canonicalStep: string;
  observations: number;
  meanWorkdaysSinceReceipt: number | null;
  spreadWorkdays: number | null;
  state: StepTimingState;
}

export interface ProcessSummary {
  name: CanonicalProcessName;
  openProductCount: number;
  funnel: FunnelStep[];
  coverageState: CoverageState;
  kpiSummary: KpiSummary;
  stagnation: Stagnation;
  /** Story 7 (CAP-10): niet alle orders zijn opgehaald; de ordercijfers zijn te laag. */
  ordersTruncated?: boolean;
}

/** spec-processen-actuele-stand — de fysieke tegel: producten op lopende orders, met wat daarover te zeggen valt. */
export interface PhysicalSummary extends ProcessSummary {
  sourceEventTotal?: number;
  /** Alle producten zonder klantsleutel die bij een klantfilter buiten de telling vallen. */
  withoutCustomerKey?: number;
  /** Fysieke producten die niet aan een tradeItem te koppelen zijn; tellen niet mee, staan apart. */
  unlinkedCount?: number;
}

/** De vier werkproces-stages waarover de overkoepelende band (blik 1) telt. */
export interface WerkprocesFunnel {
  intake: number;
  verwerking: number;
  kwaliteit: number;
  oplevering: number;
}

export interface OverkoepelendAggregate {
  delivered: number;
  running: number;
  stagnating: number;
  notStarted: number;
  /** Producten waarvan geen actieve dienst een bekende stap meldt (CAP-11); telt niet in `funnel`. */
  unknownStep: number;
  /** De effectieve drempel: uit de filterbalk, anders de ingestelde (CAP-10). */
  stagnationThresholdWorkdays: number;
  funnel: WerkprocesFunnel;
  /** Story 7 (CAP-10): niet alle orders zijn opgehaald; de ordercijfers zijn te laag. */
  ordersTruncated?: boolean;
}

export interface OverviewData {
  /** Blik 1 — de overkoepelende werkproces-band over alle diensten heen. */
  overview: OverkoepelendAggregate;
  /** Blik 2 — de aparte fysieke voortgang, náást de diensten. */
  physical: PhysicalSummary;
  /** De fysieke keten als ladder — dezelfde afleiding als het doelbeeld, server-side gemaakt. */
  physicalChain: PhysicalTargetStep[];
  /** Blik 2 — de acht diensten; de frontend groepeert ze in digitaal en fysiek. */
  diensten: ProcessSummary[];
  fetchedAt: string;
  sources: Sources;
}

export interface OverviewEnvelope {
  data: OverviewData;
}

export interface ProcessDetailData {
  name: CanonicalProcessName;
  steps: FunnelStep[];
  coverageState: CoverageState;
  leadTime: LeadTime;
  kpiSummary: KpiSummary;
  /** Producten die nog niet klaar zijn; voor `physical` geteld via de gebeurtenissen. */
  openProductCount: number;
  stagnation: Stagnation;
  /** Story 3.3 — de knelpunt-stap binnen dit proces; `null` bij nul stilstand. */
  bottleneck: BottleneckStep | null;
  /** Story 7 (CAP-10): niet alle orders zijn opgehaald; de ordercijfers zijn te laag. */
  ordersTruncated?: boolean;
  /** Story 4.1 (FR11/FR21) — gemiddelde werkdagen-vanaf-ontvangst per stap, uit een eigen `/events`-scan (eigen cache-TTL, zie `sources.procesManagementEvents`). */
  stepTimings: StepTiming[];
  fetchedAt: string;
  sources: Sources;
}

export interface ProcessDetailEnvelope {
  data: ProcessDetailData;
}

/** Story 2.1's shared per-tile classification (AD-18) — identical shape for the strip (2.3) and the dekkingsoverzicht (2.4). */
export interface CoverageTile {
  subprocesKey: string;
  canonicalStep: string | null;
  count: number | null;
  coverageState: CoverageState;
  knownCause?: string;
}

/** R2 — één sport van de fysieke ladder; `count`/`canonicalStep` zijn null als de bron de stap niet kent. */
export interface PhysicalTargetStep {
  key: string;
  canonicalStep: string | null;
  count: number | null;
  coverageState: CoverageState;
}

export interface PhysicalTargetData {
  steps: PhysicalTargetStep[];
  totalProducts: number;
  fetchedAt: string;
  sources: Sources;
}

/** R3 — één product in het fysieke proces. Twee klokken: `workdaysInStep` informatief, `stagnating` per AD-24. */
export interface PhysicalItem {
  tradeItemId: string;
  gln: string | null;
  gtin: string | null;
  targetMarket: string | null;
  stepKey: string;
  canonicalStep: string;
  since: string;
  workdaysInStep: number | null;
  notReceived: boolean;
  stagnating: boolean;
  /** Brok 4 — de gebeurtenissen van dit product, oudste eerst, begrensd op de nieuwste twintig. */
  timeline: { canonicalStep: string; at: string }[];
  timelineTruncated: boolean;
  /** spec-fysiek-stapstatus — per ketenstap de status van die stap; ontbreekt bij de dienstlijsten of een leesfout. */
  stepStatuses?: Record<string, StepStatus>;
}

export type StepStatus = 'done' | 'open' | 'notApplicable';

export interface PhysicalItemsData {
  items: PhysicalItem[];
  /** De ketenstappen op volgorde; leeg voor de acht diensten. */
  chain: string[];
  /** Aantal producten per stap over de héle verzameling, niet over de getoonde pagina. */
  stepTotals: Record<string, number>;
  totals: { items: number; notReceived: number; stagnating: number; stagnationThresholdWorkdays: number };
  meta: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
    scope: 'bounded';
    truncated: boolean;
    sourceEventTotal: number;
    withoutCustomerKey?: number;
    unlinkedCount?: number;
    /** spec-tabelfilters story 7: kolomfacetten, suggesties en de afgekapt-vlag. */
    facetten?: Record<string, Facet[]>;
    suggesties?: Suggesties;
    afgekapt?: boolean;
  };
  fetchedAt: string;
  sources: Sources;
}

/** Story 3 — de filters die de productenlijst van het fysieke proces kent (periode en status niet). */
export type PhysicalItemsFilters = Pick<ProcessFilterValues, 'gln' | 'stilstandDrempelWerkdagen'>;

export interface PhysicalItemsEnvelope {
  data: PhysicalItemsData;
}

export interface PhysicalTargetEnvelope {
  data: PhysicalTargetData;
}

/** Story 2.4 — per-RAW-canonical-step dekking (niet de subprocess-tegel uit Story 2.3). */
export interface StepCoverageEntry {
  canonicalStep: string;
  count: number | null;
  coverageState: CoverageState;
  knownCause?: string;
  lastEventAt: string | null;
}

export interface ProcessCoverageEntry {
  name: CanonicalProcessName;
  steps: StepCoverageEntry[];
}

/** Denominator-let-op (Story 2.4 AC1): dit slaat op het fysieke doelbeeld (10), niet op de 22 canonieke stappen. */
export interface PhysicalTargetSummary {
  denominator: 10;
  covered: number;
}

export interface CoverageData {
  processes: ProcessCoverageEntry[];
  physicalTargetSummary: PhysicalTargetSummary;
  /** Ruwe canonieke stap-codes met echte activiteit die door GEEN mapping-config-entry gedekt worden (FR14). */
  unmapped: string[];
  fetchedAt: string;
  sources: Sources;
}

export interface CoverageEnvelope {
  data: CoverageData;
}

/** Story 3.1: bouwt de gedeelde filter-queryparameters + eventueel een extra flag (bv. `refresh`) tot één querystring. */
function buildQuery(filters: ProcessFilterValues, extra: Record<string, string> = {}): string {
  const filterQuery = toProcessFiltersQuery(filters);
  const params = new URLSearchParams(filterQuery);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Procesoverzicht-landing (FR1): per canoniek proces een funnel-samenvatting + kernstuurcijfers. */
export async function getProcessesOverview(options: { refresh?: boolean; filters?: ProcessFilterValues } = {}): Promise<OverviewEnvelope> {
  const query = buildQuery(options.filters ?? {}, options.refresh ? { refresh: 'true' } : {});
  const response = await apiClient.get<OverviewEnvelope>(`/processes${query}`);
  return response.data;
}

/**
 * Funnel + doorlooptijd-KPI per canoniek proces (FR3/FR10/FR21).
 * Story 4.2 (AD-22): `refreshEvents` is de EIGEN ververs-affordance van het StepTransitionTiming-
 * blok — invalideert uitsluitend de zware `/events`-scan-cache voor dit proces, nooit de
 * funnel/kpi-cache (die deelt deze vlag niet met de overview-pagina's `refresh`-actie).
 */
export async function getProcessDetail(name: CanonicalProcessName, filters: ProcessFilterValues = {}, options: { refreshEvents?: boolean } = {}): Promise<ProcessDetailEnvelope> {
  const query = buildQuery(filters, options.refreshEvents ? { refreshEvents: 'true' } : {});
  const response = await apiClient.get<ProcessDetailEnvelope>(`/processes/${encodeURIComponent(name)}/status${query}`);
  return response.data;
}

/** Fysiek doelbeeld — de fysieke keten per klant (spec-processen-actuele-stand, CAP-4). `refresh` legt de gedeelde set lopende orders leeg. */
export async function getPhysicalTarget(options: { gln?: string; refresh?: boolean } = {}): Promise<PhysicalTargetEnvelope> {
  const query = buildQuery({ gln: options.gln }, options.refresh ? { refresh: 'true' } : {});
  const response = await apiClient.get<PhysicalTargetEnvelope>(`/processes/doelbeeld${query}`);
  return response.data;
}

/** Event-dekkingsoverzicht — per proces per canonieke stap (FR13/FR14, Story 2.4). */
export async function getCoverage(): Promise<CoverageEnvelope> {
  const response = await apiClient.get<CoverageEnvelope>('/processes/coverage');
  return response.data;
}

// --- Story 3.4 — stap-detailscherm op trade-item-niveau ---

export interface Progress {
  x: number;
  y: number;
}

export interface StepItemRow {
  tradeItemId: string;
  gtin: string;
  description: string | null;
  orderNumber: string;
  gln: string | null;
  service: string;
  receivedAt: string | null;
  workdaysSinceReceipt: number | null;
  kpiState: KpiState;
  stagnant: boolean;
  progress: Progress;
}

export interface StepItemsSummary {
  productCount: number;
  orderCount: number;
  meanWorkdays: number | null;
  pctWithinKpi: number | null;
  overKpiCount: number;
  stuckCount: number;
  notReceivedCount: number;
}

export interface StepItemsData {
  name: CanonicalProcessName;
  step: string;
  items: StepItemRow[];
  page: number;
  pageSize: number;
  hasMore: boolean;
  summary: StepItemsSummary;
  coverageState: CoverageState;
  /** Story 7 (CAP-10): niet alle orders zijn opgehaald; de lijst mist mogelijk producten. */
  ordersTruncated?: boolean;
  /** spec-tabelfilters story 7: alleen op het dienstpad; het fysieke pad kent geen totaal. */
  total?: number;
  facetten?: Record<string, Facet[]>;
  suggesties?: Suggesties;
  afgekapt?: boolean;
  fetchedAt: string;
  sources: Sources;
}

export interface StepItemsEnvelope {
  data: StepItemsData;
}

/** Stap-detailscherm (FR6/FR9/FR21, Story 3.4) — trade-item-rijen voor (proces, stap), server-gepagineerd over de bounded bulk-fetch. `step` wordt URL-encoded (packshot's ruwe stapcodes bevatten een letterlijke "/"). */
export async function getStepItems(
  name: CanonicalProcessName,
  step: string,
  options: { filters?: ProcessFilterValues; page?: number; pageSize?: number; kolommen?: Record<string, string> } = {},
): Promise<StepItemsEnvelope> {
  // spec-tabelfilters story 8: kolomfilters, sortering en facetten gaan ongewijzigd mee.
  const extra: Record<string, string> = { ...options.kolommen };
  if (options.page != null) extra.page = String(options.page);
  if (options.pageSize != null) extra.pageSize = String(options.pageSize);
  const query = buildQuery(options.filters ?? {}, extra);
  const response = await apiClient.get<StepItemsEnvelope>(`/processes/${encodeURIComponent(name)}/steps/${encodeURIComponent(step)}${query}`);
  return response.data;
}

// --- Story 5.1 — order-portfolio over alle orders (FR12, steunlaag) ---

/** Per-dienst `Progress{x,y}` binnen ÉÉN order-portfolio-rij (order-niveau, niet trade-item-niveau zoals `StepItemRow.progress`). */
export interface PortfolioServiceProgress {
  serviceCode: string;
  progress: Progress;
}

/** Story 5.1 (FR12, AD-14/15/21/24) — één rij per ORDER, `listOrders`-passthrough over ALLE canonieke processen. `customer` is de bare GLN (zelfde vereenvoudiging als de stap-itemlijst's "Klant"-kolom). */
export interface PortfolioRow {
  orderNumber: string;
  customer: string | null;
  services: string[];
  progress: PortfolioServiceProgress[];
  lastActivity: string | null;
  leadTime: LeadTime;
  stagnation: Stagnation;
}

export interface PortfolioData {
  items: PortfolioRow[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  fetchedAt: string;
  sources: Sources;
}

export interface PortfolioEnvelope {
  data: PortfolioData;
}

/** Order-portfolio-steunlaag (FR12, Story 5.1) — de procesfunnel blijft de primaire ingang; dit is de order-brede steunlaag eronder, `listOrders`-passthrough server-gepagineerd. */
export async function getPortfolio(filters: ProcessFilterValues = {}, page?: number, kolommen: Record<string, string> = {}): Promise<PortfolioEnvelope> {
  // spec-tabelfilters story 11: `sort` en `pageSize` uit de lijststand, naast de Processen-filters.
  const extra: Record<string, string> = { ...kolommen };
  if (page != null) extra.page = String(page);
  const query = buildQuery(filters, extra);
  const response = await apiClient.get<PortfolioEnvelope>(`/processes/portfolio${query}`);
  return response.data;
}

/** De producten in één proces, server-gepagineerd over de begrensde scan. Zonder naam: het fysieke. */
export async function getPhysicalItems(
  options: { name?: CanonicalProcessName; page?: number; pageSize?: number; refresh?: boolean; step?: string; kolommen?: Record<string, string> } & PhysicalItemsFilters = {},
): Promise<PhysicalItemsEnvelope> {
  // Klant en drempel via dezelfde bouwer als de andere Processen-routes: lege velden vallen weg.
  const params = new URLSearchParams(toProcessFiltersQuery({ gln: options.gln, stilstandDrempelWerkdagen: options.stilstandDrempelWerkdagen }));
  for (const [sleutel, waarde] of Object.entries(options.kolommen ?? {})) params.set(sleutel, waarde);
  if (options.step != null) params.set('step', options.step);
  if (options.page != null) params.set('page', String(options.page));
  if (options.pageSize != null) params.set('pageSize', String(options.pageSize));
  if (options.refresh) params.set('refresh', 'true');
  const query = params.toString();
  const pad = options.name == null ? '/processes/physical/items' : `/processes/${options.name}/items`;
  const response = await apiClient.get<PhysicalItemsEnvelope>(`${pad}${query ? `?${query}` : ''}`);
  return response.data;
}

/** Brok 5 — de gebeurtenissenstroom van één proces, nieuwste eerst. */
export interface ProcessEvent {
  tradeItemId: string;
  gtin: string | null;
  canonicalStep: string;
  at: string;
}

export interface ProcessEventsData {
  events: ProcessEvent[];
  truncated: boolean;
  sourceEventTotal: number;
  fetchedAt: string;
  sources: Sources;
}

export interface ProcessEventsEnvelope {
  data: ProcessEventsData;
}

export async function getProcessEvents(name: CanonicalProcessName, options: { limit?: number } = {}): Promise<ProcessEventsEnvelope> {
  const params = new URLSearchParams();
  if (options.limit != null) params.set('limit', String(options.limit));
  const query = params.toString();
  const response = await apiClient.get<ProcessEventsEnvelope>(`/processes/${name}/events${query ? `?${query}` : ''}`);
  return response.data;
}
